import React from 'react';
import { TrendingUp, Calculator } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для быстрой настройки параметров рентабельности
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  setErrors,
  isCalculating,
  industryTypes
}) => {
  // Парсинг числового значения или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = 0) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Текущие значения из формы
  const revenue = parseFloatOrDefault(formData.revenue);
  const costOfGoods = parseFloatOrDefault(formData.costOfGoods);
  const operatingExpenses = parseFloatOrDefault(formData.operatingExpenses);
  const grossProfitMargin = revenue > 0 ? ((revenue - costOfGoods) / revenue) * 100 : 0;
  const netProfitMargin = revenue > 0 ? ((revenue - costOfGoods - operatingExpenses) * (1 - parseFloatOrDefault(formData.taxRate) / 100) / revenue) * 100 : 0;
  
  // Обработчики изменения значений слайдеров
  const handleRevenueChange = (e) => {
    const newRevenue = parseFloat(e.target.value);
    setFormData(prev => ({ ...prev, revenue: newRevenue.toString() }));
    
    // Очищаем ошибку, если она есть
    if (errors.revenue) {
      const { revenue, ...restErrors } = errors;
      setErrors(restErrors);
    }
  };
  
  const handleCostOfGoodsChange = (e) => {
    const newCostOfGoods = parseFloat(e.target.value);
    setFormData(prev => ({ ...prev, costOfGoods: newCostOfGoods.toString() }));
    
    // Очищаем ошибку, если она есть
    if (errors.costOfGoods) {
      const { costOfGoods, ...restErrors } = errors;
      setErrors(restErrors);
    }
  };
  
  const handleOperatingExpensesChange = (e) => {
    const newOperatingExpenses = parseFloat(e.target.value);
    setFormData(prev => ({ ...prev, operatingExpenses: newOperatingExpenses.toString() }));
    
    // Очищаем ошибку, если она есть
    if (errors.operatingExpenses) {
      const { operatingExpenses, ...restErrors } = errors;
      setErrors(restErrors);
    }
  };
  
  const handleTaxRateChange = (e) => {
    const newTaxRate = parseFloat(e.target.value);
    setFormData(prev => ({ ...prev, taxRate: newTaxRate.toString() }));
    
    // Очищаем ошибку, если она есть
    if (errors.taxRate) {
      const { taxRate, ...restErrors } = errors;
      setErrors(restErrors);
    }
  };
  
  // Быстрый выбор отрасли
  const handleSelectIndustry = (industryId) => {
    setFormData(prev => ({ ...prev, industryType: industryId }));
  };
  
  // Предопределенные значения для быстрого выбора типа отрасли
  const popularIndustries = [
    'retail',
    'services',
    'manufacturing',
    'it'
  ];
  
  // Функция для вызова расчета
  const calculateProfitability = () => {
    // Здесь просто эмулируем нажатие кнопки расчета
    document.querySelector('button[type="submit"]')?.click();
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-emerald-500" />
        Быстрый подбор параметров
      </h3>
      
      <div className="space-y-5">
        {/* Выручка */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="revenueSlider" className="text-gray-600 dark:text-gray-400">Выручка</label>
            <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(revenue)}</span>
          </div>
          <input
            type="range"
            id="revenueSlider"
            min="100000"
            max="10000000"
            step="50000"
            value={revenue}
            onChange={handleRevenueChange}
            aria-label="Выручка"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-emerald-600"
          />
          
          {/* Быстрый выбор выручки */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {['1 млн', '5 млн', '10 млн', '50 млн'].map((label, index) => {
              const value = (index + 1) * (index < 2 ? 1000000 : (index === 2 ? 10000000 : 50000000));
              return (
                <button
                  key={label}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, revenue: value.toString() }))}
                  className="px-2 py-1 text-xs rounded border transition-colors bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600"
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
        
        {/* Себестоимость */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="costOfGoodsSlider" className="text-gray-600 dark:text-gray-400">Себестоимость</label>
            <div className="text-right">
              <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(costOfGoods)}</span>
              <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">
                ({Math.round((costOfGoods / revenue) * 100) || 0}% от выручки)
              </span>
            </div>
          </div>
          <input
            type="range"
            id="costOfGoodsSlider"
            min="0"
            max={revenue * 0.95}
            step={revenue * 0.01}
            value={costOfGoods}
            onChange={handleCostOfGoodsChange}
            aria-label="Себестоимость"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-emerald-600"
          />
          
          {/* Быстрый выбор процентов от выручки */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {[30, 50, 60, 70].map(percent => (
              <button
                key={percent}
                type="button"
                onClick={() => setFormData(prev => ({ 
                  ...prev, 
                  costOfGoods: Math.round(revenue * percent / 100).toString() 
                }))}
                className="px-2 py-1 text-xs rounded border transition-colors bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600"
              >
                {percent}%
              </button>
            ))}
          </div>
        </div>
        
        {/* Операционные расходы */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="operatingExpensesSlider" className="text-gray-600 dark:text-gray-400">Операционные расходы</label>
            <div className="text-right">
              <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(operatingExpenses)}</span>
              <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">
                ({Math.round((operatingExpenses / revenue) * 100) || 0}% от выручки)
              </span>
            </div>
          </div>
          <input
            type="range"
            id="operatingExpensesSlider"
            min="0"
            max={revenue * 0.5}
            step={revenue * 0.01}
            value={operatingExpenses}
            onChange={handleOperatingExpensesChange}
            aria-label="Операционные расходы"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-emerald-600"
          />
          
          {/* Быстрый выбор процентов от выручки */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {[10, 15, 20, 30].map(percent => (
              <button
                key={percent}
                type="button"
                onClick={() => setFormData(prev => ({ 
                  ...prev, 
                  operatingExpenses: Math.round(revenue * percent / 100).toString() 
                }))}
                className="px-2 py-1 text-xs rounded border transition-colors bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600"
              >
                {percent}%
              </button>
            ))}
          </div>
        </div>
        
        {/* Ставка налога */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="taxRateSlider" className="text-gray-600 dark:text-gray-400">Ставка налога на прибыль</label>
            <span className="font-medium text-gray-900 dark:text-white">{formData.taxRate}%</span>
          </div>
          <input
            type="range"
            id="taxRateSlider"
            min="0"
            max="30"
            step="1"
            value={formData.taxRate}
            onChange={handleTaxRateChange}
            aria-label="Ставка налога"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-emerald-600"
          />
          
          {/* Стандартные ставки налога в России */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {[6, 15, 20].map(rate => (
              <button
                key={rate}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, taxRate: rate.toString() }))}
                className={`px-2 py-1 text-xs rounded border transition-colors ${
                  parseInt(formData.taxRate) === rate 
                    ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                {rate}%
              </button>
            ))}
            <div className="text-xs text-gray-500 dark:text-gray-400 px-2 flex items-center">
              Режимы налогообложения
            </div>
          </div>
        </div>
        
        {/* Отрасль бизнеса */}
        <div>
          <div className="flex justify-between items-center mb-2 text-sm">
            <label className="text-gray-600 dark:text-gray-400">Отрасль бизнеса</label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {popularIndustries.map(industryId => {
              const industry = industryTypes.find(ind => ind.id === industryId);
              return (
                <button
                  key={industryId}
                  type="button"
                  onClick={() => handleSelectIndustry(industryId)}
                  className={`p-2 text-sm rounded-lg border transition-colors ${
                    formData.industryType === industryId 
                      ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                      : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
                >
                  {industry ? industry.name : industryId}
                </button>
              );
            })}
          </div>
        </div>
        
        {/* Расчетные показатели */}
        <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
          <div className="mb-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">Валовая маржа:</span>
              <span className={`font-medium ${grossProfitMargin >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                {grossProfitMargin.toFixed(1)}%
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">Чистая маржа:</span>
              <span className={`font-medium ${netProfitMargin >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                {netProfitMargin.toFixed(1)}%
              </span>
            </div>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Это предварительный расчет рентабельности. Для полного анализа используйте кнопку ниже.
          </p>
        </div>
        
        {/* Кнопка расчета */}
        <Button
          primary
          fullWidth
          onClick={calculateProfitability}
          icon={<TrendingUp className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать рентабельность
        </Button>
      </div>
    </div>
  );
};

export default QuickAdjustmentSidebar;